import { hasBranch, hasBranches } from 'dashboard/lib/workflows';
import { WorkflowStep, WorkflowStepType } from 'dashboard/models/workflow-version';
import { generateWorkflowToken } from 'lib/workflows';

interface TraverseWorkflowStepOutput {
  step?: WorkflowStep;
  steps?: WorkflowStep[];
  checkChildren?: boolean;
}

/**
 * @param steps: Original set of WorkflowSteps
 * @param fxn: Function to be applied to each workflow step in `steps`
 * @returns An array of WorkflowSteps after `fxn` has been applied to each step. Currently
 * the method is used to map, filter, and delete steps.
 */
export const traverseWorkflowSteps = (
  steps: WorkflowStep[],
  fxn: (step: WorkflowStep) => TraverseWorkflowStepOutput,
): WorkflowStep[] => {
  return steps.reduce<WorkflowStep[]>((result, step) => {
    const { step: newStep, steps: newSteps = [], checkChildren = true } = fxn(step);
    if (newSteps.length > 0) {
      return [...result, ...newSteps];
    }

    if (!newStep) return result;

    return [
      ...result,
      {
        ...newStep,
        ...(checkChildren && hasBranch(newStep)
          ? {
              branch: traverseWorkflowSteps(newStep.branch, fxn),
            }
          : {}),
        ...(checkChildren && hasBranches(newStep)
          ? {
              branches:
                newStep.type === WorkflowStepType.Parallel
                  ? newStep.branches
                      .map((branchSteps) => traverseWorkflowSteps(branchSteps, fxn))
                      .filter((branch) => branch.length > 0)
                  : newStep.branches.map((branchSteps) => traverseWorkflowSteps(branchSteps, fxn)),
            }
          : {}),
      },
    ];
  }, []);
};

/**
 *
 * Return new stepsConfig with replacing a step matched token
 * @param steps: An original stepsConfig
 * @param newStep: A step which will be used to replace
 *
 **/
export const findAndReplaceSteps = (
  steps: WorkflowStep[],
  newStep: WorkflowStep,
): WorkflowStep[] => {
  const findAndReplaceStep = (step: WorkflowStep) =>
    step.token === newStep.token ? { step: newStep, checkChildren: false } : { step };
  return traverseWorkflowSteps(steps, findAndReplaceStep);
};

/**
 *
 * Return new stepsConfig with omitting a step matched token. A blank branch of Parallel step will be omitted as well
 * @param steps: An original stepsConfig
 * @param targetToken: A token of a step which will be omitted
 *
 **/
export const omitStep = (steps: WorkflowStep[], targetToken: string): WorkflowStep[] => {
  const omitStep = (step: WorkflowStep) =>
    step.token === targetToken ? { checkChildren: false } : { step };
  return traverseWorkflowSteps(steps, omitStep);
};

/**
 * Return new stepConfig inserting a new step that follows a given step token
 * @param steps: An original stepsConfig
 * @param previousStepToken: The step to insert the new step after
 * @param newStep: A new step to add to the stepsConfig
 */
export const findAndAddNextSteps = (
  steps: WorkflowStep[],
  previousStepToken: string,
  newSteps: WorkflowStep[],
): WorkflowStep[] => {
  const addNextSteps = (step: WorkflowStep) =>
    step.token === previousStepToken
      ? { steps: [step, ...newSteps], checkChildren: false }
      : { step };
  return traverseWorkflowSteps(steps, addNextSteps);
};

/**
 * Returns a new stepConfig with a new step inserted at the beginning of a branch
 * @param steps: An original stepsConfig
 * @param newStep: A new step to add to the stepsConfig
 * @param parentStepToken: The parent of the branch to be inserted into
 * @param branchIndex: The index of the branch to be inserted into
 */
export const findAndAddFirstBranchStep = (
  steps: WorkflowStep[],
  newStep: WorkflowStep,
  parentStepToken: string,
  branchIndex: number,
): WorkflowStep[] => {
  const addToFirstBranch = (step: WorkflowStep) =>
    step.token === parentStepToken
      ? {
          step: {
            ...step,
            ...(hasBranch(step)
              ? {
                  branch: [newStep, ...step.branch],
                }
              : {}),
            ...(hasBranches(step)
              ? {
                  branches: [
                    ...step.branches.slice(0, branchIndex),
                    [newStep, ...step.branches[branchIndex]],
                    ...step.branches.slice(branchIndex + 1),
                  ],
                }
              : {}),
          },
          checkChildren: false,
        }
      : { step };

  return traverseWorkflowSteps(steps, addToFirstBranch);
};

/**
 * Returns an array of steps with branch steps removed if not selected
 * @param steps: Top level steps that have been selected. This does not necessarily
 * mean that its branch steps should be used.
 * @param stepAvailableTracker: A tracker to keep track of all steps to be used.
 */
export const filterSteps = (
  steps: WorkflowStep[],
  stepAvailableTracker: Record<string, boolean>,
): WorkflowStep[] => {
  const tracker = { ...stepAvailableTracker };
  const filterStep = (step: WorkflowStep) => {
    if (tracker[step.token]) {
      tracker[step.token] = false;
      return { step };
    }
    return {};
  };

  return traverseWorkflowSteps(steps, filterStep);
};

const generateWorkflowTokenWithRecord = (
  oldToken: string,
  tokenMap: Map<string, string>,
): string => {
  const newToken = generateWorkflowToken();
  tokenMap.set(oldToken, newToken);
  return newToken;
};

/**
 * @param steps: Initial set of steps
 * @returns `steps` but with new tokens
 */
export const replaceWithNewTokens = (
  steps: WorkflowStep[],
): {
  newSteps: WorkflowStep[];
  tokenMap: Map<string, string>;
} => {
  const tokenMap = new Map<string, string>();
  const replaceToken = (step: WorkflowStep) => ({
    step: {
      ...step,
      token: generateWorkflowTokenWithRecord(step.token, tokenMap),
      ...(WorkflowStepType.Conditional === step.type
        ? {
            conditionals: step.conditionals.map((conditional) => ({
              ...conditional,
              token: generateWorkflowTokenWithRecord(conditional.token, tokenMap),
            })),
          }
        : {}),
    },
  });
  const newSteps = traverseWorkflowSteps(steps, replaceToken);
  return { newSteps, tokenMap };
};
